// src/pages/ServicesPage.js
import React, { useContext, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FloatingContactContext } from '../components/FloatingContact';
import '../styles/ServicesPage.css';
import env from '../config/env'; // Import environment config

const ServicesPage = () => {
  const { handleOpenContact } = useContext(FloatingContactContext);
  const location = useLocation();
  const llcRef = useRef(null);
  const corporationRef = useRef(null);
  const complianceRef = useRef(null);

  // Define S3 image URLs
  const serviceImages = {
    llc: `${env.aws.bucketName}.s3.${env.aws.region}.amazonaws.com/images/services/llc-formation.jpg`,
    corporation: `${env.aws.bucketName}.s3.${env.aws.region}.amazonaws.com/images/services/corporation-setup.jpg`,
    compliance: `${env.aws.bucketName}.s3.${env.aws.region}.amazonaws.com/images/services/compliance-services.jpeg`
  };

  // Scroll to the appropriate section when the component mounts or location changes
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.substring(1); // Remove the # character
      
      switch(hash) {
        case 'llc':
          if (llcRef.current) {
            llcRef.current.scrollIntoView({ behavior: 'smooth' });
          }
          break;
        case 'corporation':
          if (corporationRef.current) {
            corporationRef.current.scrollIntoView({ behavior: 'smooth' });
          }
          break;
        case 'compliance':
          if (complianceRef.current) {
            complianceRef.current.scrollIntoView({ behavior: 'smooth' });
          }
          break;
        default:
          window.scrollTo(0, 0);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="services-page">
      {/* Services Header */}
      <section className="services-header">
        <div className="container">
          <h1 className="services-title">Our Services</h1>
          <p className="services-description">
            We help entrepreneurs and small business owners form legal structures that 
            protect their assets and reduce their tax burden.
          </p>
          <p className="services-cta-text">
            Explore our services below or contact us to get started.
          </p>
        </div>
      </section>
      
      {/* LLC Formation */}
      <section id="llc" ref={llcRef} className="section service-detail-section">
        <div className="container">
          <div className="service-detail">
            <div className="service-icon-wrapper">
              <i className="fas fa-building"></i>
            </div>
            <div className="service-content">
              <h2 className="service-title">LLC Formation</h2>
              <p className="service-ideal">
                <strong>Ideal for:</strong> Freelancers, solopreneurs, small business owners
              </p>
              <div className="service-includes">
                <h3>What's included:</h3>
                <ul className="service-features">
                  <li>Business name registration</li>
                  <li>EIN (Tax ID) application</li>
                  <li>Custom Operating Agreement</li>
                  <li>State filings and documentation</li>
                  <li>Compliance calendar</li>
                  <li>30-day post-formation support</li>
                </ul>
              </div>
              <Link to="/service-form/llc" className="btn btn-primary">Start My LLC</Link>
            </div>
            <div className="service-image">
              <img 
                src={`https://${serviceImages.llc}`} 
                alt="LLC Formation" 
                className="service-img" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentNode.classList.add('placeholder-image');
                }}
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Corporation Setup */}
      <section id="corporation" ref={corporationRef} className="section service-detail-section alt-bg">
        <div className="container">
          <div className="service-detail reverse">
            <div className="service-icon-wrapper">
              <i className="fas fa-landmark"></i>
            </div>
            <div className="service-content">
              <h2 className="service-title">Corporation Setup</h2>
              <p className="service-ideal">
                <strong>Ideal for:</strong> High-income entrepreneurs or those scaling operations
              </p>
              <div className="service-includes">
                <h3>What's included:</h3>
                <ul className="service-features">
                  <li>Everything in LLC formation</li>
                  <li>S-Corporation or C-Corporation election paperwork</li>
                  <li>Shareholder agreements and bylaws</li>
                  <li>Corporate governance structure</li>
                  <li>Board meeting templates</li>
                  <li>60-day post-formation support</li>
                </ul>
              </div>
              <Link to="/service-form/corporation" className="btn btn-primary">Start My Corporation</Link>
            </div>
            <div className="service-image">
              <img 
                src={`https://${serviceImages.corporation}`} 
                alt="Corporation Setup" 
                className="service-img" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentNode.classList.add('placeholder-image');
                }}
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Compliance Services */}
      <section id="compliance" ref={complianceRef} className="section service-detail-section">
        <div className="container">
          <div className="service-detail">
            <div className="service-icon-wrapper">
              <i className="fas fa-clipboard-check"></i>
            </div>
            <div className="service-content">
              <h2 className="service-title">Compliance Services</h2>
              <p className="service-ideal">
                <strong>Ideal for:</strong> Established businesses looking for ongoing legal support
              </p>
              <div className="service-includes">
                <h3>What's included:</h3>
                <ul className="service-features">
                  <li>Annual report preparation and filing</li>
                  <li>State compliance monitoring</li>
                  <li>Business license renewals</li>
                  <li>Regulatory change alerts</li>
                  <li>Compliance health check assessments</li>
                  <li>Ongoing legal support</li>
                </ul>
              </div>
              <Link to="/service-form/compliance" className="btn btn-primary">Stay Compliant</Link>
            </div>
            <div className="service-image">
              <img 
                src={`https://${serviceImages.compliance}`} 
                alt="Compliance Services" 
                className="service-img" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentNode.classList.add('placeholder-image');
                }}
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Form Your Business?</h2>
            <p className="cta-text">
              Take the first step toward protecting your assets and setting your business up for success.
            </p>
            <div className="cta-buttons">
              <Link to="/service-form" className="btn btn-secondary">Start Your Business</Link>
              <button onClick={handleOpenContact} className="btn btn-outline-light">Free Consultation</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
